import React, { useMemo, useState } from "react";
import { UPDATE_USER_MUTATION } from "src/constants/graphql";
import useAuth from "src/hooks/useAuth";
import useNotification from "src/hooks/notification";
import useMutation from "src/hooks/useMutation";
import { useConfigurableFields } from "src/contexts/configurable-fields";
import pick from "lodash/pick";
import keyBy from "lodash/keyBy";
import isEmpty from "lodash/isEmpty";

const USER_FIELDS = ["id", "width", "isFrozen", "visible"];

const isCustomField = field => field?.id?.startsWith("customField");

const customFieldSettings = customFields =>
  (customFields || []).map(field => ({
    id: `customField${field.id}`,
    visible: false,
  }));

const preserveMissingSavedCustomFields = (fields, userSettings, customFields = []) => {
  const savedFields = userSettings?.fields || [];
  const knownCustomFields = customFieldSettings(customFields);
  const knownCustomFieldIds = new Set(knownCustomFields.map(field => field.id));
  const hasKnownCustomFields = knownCustomFields.length > 0;
  const mergedFields = [...fields];
  const mergedFieldIds = new Set(mergedFields.map(field => field.id));

  savedFields.forEach((field, index) => {
    if (!isCustomField(field) || mergedFieldIds.has(field.id)) return;
    if (hasKnownCustomFields && !knownCustomFieldIds.has(field.id)) return;

    mergedFields.splice(Math.min(index, mergedFields.length), 0, field);
    mergedFieldIds.add(field.id);
  });

  knownCustomFields.forEach(field => {
    if (mergedFieldIds.has(field.id)) return;

    mergedFields.push(field);
    mergedFieldIds.add(field.id);
  });

  return mergedFields;
};

export const buildStoreSettings = (newSettings, userSettings, customFields = []) => ({
  ...newSettings,
  fields: preserveMissingSavedCustomFields(newSettings.fields, userSettings, customFields).map(
    field => pick(field, ...USER_FIELDS)
  ),
});

const mergeSettings = (defaultSettings, userSettings) => {
  if (isEmpty(userSettings)) return defaultSettings;
  if (defaultSettings.version !== userSettings.version) return defaultSettings;

  const settings = { ...defaultSettings, ...userSettings };
  const userFields = keyBy(userSettings.fields, "id");
  const defaultFields = keyBy(defaultSettings.fields, "id");
  const defaultFieldsIds = Object.keys(defaultFields);

  const newFields = defaultSettings.fields.filter(
    field => !Object.keys(userFields).includes(field.id)
  );

  const mergedFields = userSettings.fields.reduce((acc, field) => {
    if (defaultFieldsIds.includes(field.id)) {
      const merged = {
        ...defaultFields[field.id],
        ...pick(userFields[field.id], USER_FIELDS),
      };
      // Cap BULK_CHECKBOX width to prevent it from being too wide
      if (merged.id === "BULK_CHECKBOX") {
        merged.width = Math.min(merged.width || 50, 50);
      }
      return [...acc, merged];
    } else if (isCustomField(field)) {
      return [...acc, field];
    } else {
      return acc;
    }
  }, []);

  return {
    ...settings,
    fields: [...mergedFields, ...newFields],
  };
};

const useTablePro = ({ key, default: defaultSettings, setCollection }) => {
  const { user, setUser } = useAuth();
  const { customFields = [] } = useConfigurableFields() || {};
  const { notifyError } = useNotification();
  const [updateUser] = useMutation(UPDATE_USER_MUTATION);
  const [cacheVersion, setCacheVersion] = useState(1);
  const [disabledScroll, setDisabledScroll] = useState(false);

  const [settings, setSettings] = useState(
    key ? mergeSettings(defaultSettings, user[key]) : defaultSettings
  );

  const handleSetSettings = newSettings => {
    setSettings(newSettings);
    setCacheVersion(prev => prev + 1);
  };

  const updateSettings = newProps => {
    const oldSettings = { ...settings };
    const newSettings = { ...settings, ...newProps };

    handleSetSettings(newSettings);

    const storeSettings = buildStoreSettings(
      newSettings,
      user[key],
      key === "assetsTableSettings" ? customFields : []
    );

    if (key) {
      return updateUser({
        variables: { [key]: storeSettings },
        onSuccess: data => {
          setUser({ ...user, [key]: data.user[key] });
        },
        onFailure: errors => {
          notifyError(errors);
          handleSetSettings(oldSettings);
        },
      });
    }
  };

  const fields = useMemo(() => settings.fields, [cacheVersion]);

  return useMemo(
    () => ({
      limit: settings.limit,
      size: settings.size,
      fields: fields,
      order: settings.order,
      updateSettings,
    }),
    [cacheVersion]
  );
};

export default useTablePro;
